/**
 * Control Initializer
 * Registers all available control components with the ControlRegistry.
 * Each registration includes the runtime renderer AND SchemaBuilder UI metadata
 * (icon, color, hasChilds, settingsConfig) as the single source of truth.
 * Call this once during app initialization.
 */

import React from 'react';
import {
  TextFields,
  Tag,
  ToggleOn,
  ArrowDropDownCircle,
  DoneAll,
  GridView,
  CalendarToday,
  AccessTime,
  Notes,
  CloudUpload,
  FormatColorText,
  FormatListBulleted,
  ViewStream,
  AccountTree,
  Palette,
  Article,
  Tab as TabLayoutIcon,
} from '@mui/icons-material';

import { registerControl } from './ControlRegistry';
import { ControlType } from '../../../../types/ControlTypes';

// Import control components
import { StringControl } from './StringControl';
import { NumberControl } from './NumberControl';
import { BooleanControl } from './BooleanControl';
import { SelectControl } from './SelectControl';
import { MultiselectControl } from './MultiselectControl';
import { BlockControl } from './BlockControl';
import { DateControl } from './DateControl';
import { TextareaControl } from './TextareaControl';
import { UploadControl } from './UploadControl';
import { RichTextControl } from './RichTextControl';
import { ArrayControl } from './ArrayControl';
import { CollapseControl } from './CollapseControl';
import { LookupControl } from './LookupControl';
import { DatetimeControl } from './DatetimeControl';
import { ColorControl } from './ColorControl';
import { ToggleControl } from './ToggleControl';
import { TabControl } from './TabControl';

/**
 * Initialize all control components.
 * Should be called once during app startup (e.g. at the top of SchemaFormBuilder).
 */
export function initializeControls(): void {
  // ─── Tier 1: Core Controls ───────────────────────────────────────────────
  registerControl(ControlType.STRING, StringControl as any, 'Text Input', {
    description: 'Single-line or multi-line text input',
    tier: 'core',
    icon: <TextFields fontSize="small" />,
    color: '#4CAF50',
    defaultJsonType: 'string',
    settingsConfig: [
      { key: 'placeholder', label: 'Placeholder',   type: 'text' },
      { key: 'multiline',   label: 'Multiline',     type: 'toggle' },
      { key: 'rows',        label: 'Rows',           type: 'number', showIf: (s) => !!s.multiline },
      { key: 'maxLength',   label: 'Max Length',    type: 'number' },
      { key: 'minLength',   label: 'Min Length',    type: 'number' },
      { key: 'pattern',     label: 'Regex Pattern', type: 'text', placeholder: '^https?://.*' },
    ],
  });

  registerControl(ControlType.NUMBER, NumberControl as any, 'Number', {
    description: 'Numeric input with min/max constraints',
    tier: 'core',
    icon: <Tag fontSize="small" />,
    color: '#2196F3',
    defaultJsonType: 'number',
    settingsConfig: [
      { key: 'placeholder', label: 'Placeholder', type: 'text' },
      { key: 'minimum',     label: 'Minimum',      type: 'number' },
      { key: 'maximum',     label: 'Maximum',      type: 'number' },
      { key: 'step',        label: 'Step',         type: 'number' },
      { key: 'unit',        label: 'Unit',         type: 'text', placeholder: 'USD, px, ms...' },
    ],
  });

  registerControl(ControlType.BOOLEAN, BooleanControl as any, 'Boolean', {
    description: 'Boolean toggle or checkbox',
    tier: 'core',
    icon: <ToggleOn fontSize="small" />,
    color: '#9C27B0',
    defaultJsonType: 'boolean',
    settingsConfig: [
      { key: 'label', label: 'Label Override', type: 'text' },
    ],
  });

  registerControl(ControlType.SELECT, SelectControl as any, 'Select', {
    description: 'Single-select dropdown with static or dynamic options',
    tier: 'core',
    icon: <ArrowDropDownCircle fontSize="small" />,
    color: '#FF9800',
    defaultJsonType: 'string',
    settingsConfig: [
      { key: 'placeholder', label: 'Placeholder',      type: 'text' },
      { key: 'options',     label: 'Static Options',   type: 'options' },
      { key: 'datasource',  label: 'API Datasource',   type: 'text', placeholder: 'collection-name' },
      { key: 'renderProp',  label: 'Display Property', type: 'text', placeholder: 'name' },
      { key: 'keyProp',     label: 'Value Property',   type: 'text', placeholder: 'id' },
    ],
  });

  registerControl(ControlType.MULTISELECT, MultiselectControl as any, 'Multi-Select', {
    description: 'Multi-select dropdown',
    tier: 'core',
    icon: <DoneAll fontSize="small" />,
    color: '#FF9800',
    defaultJsonType: 'array',
    settingsConfig: [
      { key: 'placeholder', label: 'Placeholder',      type: 'text' },
      { key: 'options',     label: 'Static Options',   type: 'options' },
      { key: 'datasource',  label: 'API Datasource',   type: 'text', placeholder: 'collection-name' },
      { key: 'renderProp',  label: 'Display Property', type: 'text', placeholder: 'name' },
      { key: 'keyProp',     label: 'Value Property',   type: 'text', placeholder: 'id' },
    ],
  });

  registerControl(ControlType.BLOCK, BlockControl as any, 'Block/Group', {
    description: 'Container for nested properties',
    tier: 'core',
    icon: <GridView fontSize="small" />,
    color: '#607D8B',
    hasChilds: true,
    defaultJsonType: 'object',
    settingsConfig: [],
  });

  // ─── Tier 2: Advanced Controls ────────────────────────────────────────────
  registerControl(ControlType.DATE, DateControl as any, 'Date', {
    description: 'Date picker control',
    tier: 'advanced',
    icon: <CalendarToday fontSize="small" />,
    color: '#00BCD4',
    defaultJsonType: 'string',
    settingsConfig: [
      { key: 'format', label: 'Format', type: 'text', placeholder: 'MM/dd/yyyy' },
    ],
  });

  registerControl(ControlType.DATETIME, DatetimeControl as any, 'DateTime', {
    description: 'Date and time picker control',
    tier: 'advanced',
    icon: <AccessTime fontSize="small" />,
    color: '#00BCD4',
    defaultJsonType: 'string',
    settingsConfig: [
      { key: 'format', label: 'Format', type: 'text', placeholder: 'MM/dd/yyyy HH:mm' },
    ],
  });

  registerControl(ControlType.TEXTAREA, TextareaControl as any, 'Textarea', {
    description: 'Multi-line text area',
    tier: 'advanced',
    icon: <Notes fontSize="small" />,
    color: '#4CAF50',
    defaultJsonType: 'string',
    settingsConfig: [
      { key: 'placeholder', label: 'Placeholder', type: 'text' },
      { key: 'rows',        label: 'Rows',         type: 'number' },
      { key: 'maxLength',   label: 'Max Length',  type: 'number' },
      { key: 'minLength',   label: 'Min Length',  type: 'number' },
    ],
  });

  registerControl(ControlType.UPLOAD, UploadControl as any, 'File Upload', {
    description: 'File upload control',
    tier: 'advanced',
    icon: <CloudUpload fontSize="small" />,
    color: '#795548',
    defaultJsonType: 'string',
    settingsConfig: [
      { key: 'chooseFromAsset', label: 'Choose from Asset Library', type: 'toggle' },
      { key: 'category',        label: 'Upload Category',           type: 'category-select' },
      { key: 'accept',          label: 'Accept Types',              type: 'text', placeholder: 'image/*, .pdf' },
      { key: 'multiple',        label: 'Multiple Files',            type: 'toggle' },
      { key: 'maxSize',         label: 'Max Size (bytes)',          type: 'number' },
    ],
  });

  registerControl(ControlType.RICHTEXT, RichTextControl as any, 'Rich Text', {
    description: 'Rich text editor with formatting',
    tier: 'advanced',
    icon: <FormatColorText fontSize="small" />,
    color: '#E91E63',
    defaultJsonType: 'string',
    settingsConfig: [
      { key: 'placeholder', label: 'Placeholder', type: 'text' },
      { key: 'minHeight',   label: 'Min Height',  type: 'text', placeholder: '300px' },
    ],
  });

  registerControl(ControlType.ARRAY, ArrayControl as any, 'Array/List', {
    description: 'Array of items with add/remove/sort',
    tier: 'advanced',
    icon: <FormatListBulleted fontSize="small" />,
    color: '#FF5722',
    hasChilds: true,
    defaultJsonType: 'array',
    settingsConfig: [
      { key: 'minItems', label: 'Min Items', type: 'number' },
      { key: 'maxItems', label: 'Max Items', type: 'number' },
    ],
  });

  registerControl(ControlType.COLLAPSE, CollapseControl as any, 'Collapse', {
    description: 'Collapsible items with nested controls and add/remove',
    tier: 'advanced',
    icon: <ViewStream fontSize="small" />,
    color: '#005e5d',
    hasChilds: true,
    defaultJsonType: 'array',
    settingsConfig: [
      { key: 'minItems',        label: 'Min Items',                type: 'number' },
      { key: 'maxItems',        label: 'Max Items',                type: 'number' },
      { key: 'defaultExpanded', label: 'Default Expanded (index)', type: 'number' },
    ],
  });

  registerControl(ControlType.LOOKUP, LookupControl as any, 'Lookup', {
    description: 'Reference to another collection',
    tier: 'advanced',
    icon: <AccountTree fontSize="small" />,
    color: '#3F51B5',
    defaultJsonType: 'string',
    settingsConfig: [
      { key: 'source',      label: 'Source Collection *', type: 'text',  placeholder: 'collection-name' },
      { key: 'renderProp',  label: 'Display Property',    type: 'text',  placeholder: 'name' },
      { key: 'keyProp',     label: 'Key Property',        type: 'text',  placeholder: 'id' },
      { key: 'multiselect', label: 'Allow Multiple',       type: 'toggle' },
      { key: 'placeholder', label: 'Placeholder',          type: 'text' },
      { key: 'baseFilter',  label: 'Base Filter (JSON)',   type: 'json',  placeholder: '{"type":"and","groups":[{"field":"isPublic","op":{"$eq":true}}]}' },
    ],
  });

  // ─── Tier 3: Specialized Controls ────────────────────────────────────────
  registerControl(ControlType.COLOR, ColorControl as any, 'Color Picker', {
    description: 'Color input with picker',
    tier: 'specialized',
    icon: <Palette fontSize="small" />,
    color: '#F44336',
    defaultJsonType: 'string',
    settingsConfig: [
      { key: 'format',     label: 'Format',              type: 'select-enum', options: ['hex', 'rgb', 'hsl'] },
      { key: 'allowAlpha', label: 'Allow Alpha Channel',  type: 'toggle' },
    ],
  });

  registerControl(ControlType.TOGGLE, ToggleControl as any, 'Toggle Switch', {
    description: 'On/off toggle switch',
    tier: 'specialized',
    icon: <Article fontSize="small" />,
    color: '#9C27B0',
    defaultJsonType: 'boolean',
    settingsConfig: [
      { key: 'label', label: 'Label Override', type: 'text' },
    ],
  });

  registerControl(ControlType.TAB, TabControl as any, 'Tab Control', {
    description: 'Tab panels with named child controls',
    tier: 'specialized',
    icon: <TabLayoutIcon fontSize="small" />,
    color: '#0288D1',
    defaultJsonType: 'object',
    settingsConfig: [],
  });
}

